import { PermissionsEnum } from 'consts/permissions';
import Auth from 'modules/Auth';
import { useCallback, useMemo } from 'react';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { NAVIGATION_PATHS } from 'consts/navigation';
import { checkFeaturePermission } from 'utils/checkFeaturePermission';
import { FeatureFlags } from '../types/Features.types';
import { ItemType } from './MenuItem/MenuItem.types';
import { MENU_ITEMS } from './constants';

export const useSidebarMenu = (): ItemType[] => {
  const arePermissionsEnabled = useHasFeatureAccess(FeatureFlags.PLANNERS_PERMISSIONS);
  const hasNavigationPermissions = useHasFeatureAccess(FeatureFlags.NAVIGATION_PERMISSIONS);

  const isProgrammaticPermission = checkFeaturePermission(
    arePermissionsEnabled,
    Auth.hasPermission(PermissionsEnum.PROGRAMMATIC_PERMISSION),
  );
  const isDirectSalesPermission = checkFeaturePermission(
    arePermissionsEnabled,
    Auth.hasPermission(PermissionsEnum.DIRECT_SALES_PERMISSION),
  );

  const isDirectSalesOrProgrammaticAccess = isDirectSalesPermission || isProgrammaticPermission;

  const hasPageAccessPermission = useCallback(
    (permission: boolean): boolean => {
      if (!arePermissionsEnabled) return permission;

      if (hasNavigationPermissions) return Auth.hasPermission(PermissionsEnum.PROGRAMMATIC_PERMISSION) && permission;

      return permission;
    },
    [arePermissionsEnabled, hasNavigationPermissions],
  );

  const isDealManagementPageAccess =
    isDirectSalesOrProgrammaticAccess && Auth.hasPermission(PermissionsEnum.DEAL_MANAGEMENT_PAGE_ACCESS);

  const isContentPageAccess = hasPageAccessPermission(
    isProgrammaticPermission && Auth.hasPermission(PermissionsEnum.CONTENT_PAGE_ACCESS),
  );

  const isInsightsPageAccess = hasPageAccessPermission(isProgrammaticPermission);

  const isAdministrationPageAccess = hasPageAccessPermission(
    isProgrammaticPermission && Auth.hasPermission(PermissionsEnum.ADMINISTRATION_PAGE_ACCESS),
  );

  const isInventoryManagementPageAccess = hasPageAccessPermission(
    isProgrammaticPermission && Auth.hasPermission(PermissionsEnum.SETUP_PAGE_ACCESS),
  );

  const isAvailabilityReport =
    useHasFeatureAccess(FeatureFlags.AVAILABILITY_REPORT) && isDirectSalesOrProgrammaticAccess;

  return useMemo(() => {
    return [
      ...(isDealManagementPageAccess
        ? [
            {
              ...MENU_ITEMS.DEAL_MANAGEMENT,
              navigationPath: Auth.hasPermission(PermissionsEnum.DIRECT_SALES_PERMISSION)
                ? NAVIGATION_PATHS.DIRECT_SALES
                : NAVIGATION_PATHS.PROGRAMMATIC,
            },
          ]
        : []),
      ...(isContentPageAccess ? [MENU_ITEMS.CONTENT_MANAGEMENT] : []),
      ...(isInsightsPageAccess ? [MENU_ITEMS.INSIGHTS] : []),
      ...(isAdministrationPageAccess
        ? [
            {
              ...MENU_ITEMS.ADMINISTRATION,
              isVisible: () => {
                return hasPageAccessPermission(
                  isProgrammaticPermission && Auth.hasPermission(PermissionsEnum.ADMINISTRATION_PAGE_ACCESS),
                );
              },
            },
          ]
        : []),
      ...(isInventoryManagementPageAccess ? [MENU_ITEMS.INVENTORY_MANAGEMENT] : []),
      ...(isAvailabilityReport ? [MENU_ITEMS.AVAILABILITY_REPORT] : []),
    ];
  }, [
    isDealManagementPageAccess,
    isContentPageAccess,
    isInsightsPageAccess,
    isAdministrationPageAccess,
    isInventoryManagementPageAccess,
    isAvailabilityReport,
    isProgrammaticPermission,
    hasPageAccessPermission,
  ]);
};
